import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'

import { feedBadge } from '@/ui/lib/feedBadge'
import { filterSymbols, groupSymbols } from '@/ui/lib/pairFilter'
import { cn } from '@/ui/lib/utils'

export interface QuantPairOption {
  symbol: string
  description: string
  group: string
  venue: string
}

export interface QuantPairDropdownProps {
  symbols: QuantPairOption[]
  /** The selected symbol (it may not be in `symbols`, e.g. an unknown URL symbol). */
  value: string | null
  onPick: (symbol: string) => void
  /** Spec 070: the feed's state, shown beside each name. A no-feed state greys the rows and states the reason; they stay selectable. */
  feedState?: string | null
  /** Text for an empty list (the feed says no instrument is live). */
  emptyText?: string
}

interface Anchor {
  left: number
  top: number
  /** The trigger's inherited `--h`, re-applied on the portal root so the accent tokens resolve. */
  hue: string
}

/**
 * Manual trade's pair picker (spec 036, handoff 4.2). The popover is portalled
 * to `document.body` (like `Select`), so it re-applies the trigger's inherited
 * `--h` on its own root. A search box (autofocused) filters by symbol or
 * description; `Enter` picks the first hit, `Esc` closes without bubbling, and
 * a click outside closes. Opening resets the query.
 */
export function QuantPairDropdown({ symbols, value, onPick, feedState = null, emptyText }: QuantPairDropdownProps) {
  const [anchor, setAnchor] = useState<Anchor | null>(null)
  const [query, setQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const open = anchor !== null
  const badge = feedBadge(feedState)
  const current = symbols.find((s) => s.symbol === value)
  const hits = filterSymbols(symbols, query)
  const groups = groupSymbols(hits)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  function openList() {
    const el = triggerRef.current
    if (el === null) return
    const rect = el.getBoundingClientRect()
    setQuery('')
    setAnchor({ left: rect.left, top: rect.bottom + 4, hue: getComputedStyle(el).getPropertyValue('--h').trim() })
  }

  function close(refocus: boolean) {
    setAnchor(null)
    setQuery('')
    if (refocus) triggerRef.current?.focus()
  }

  function pick(symbol: string) {
    close(true)
    onPick(symbol)
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && hits[0]) {
      event.preventDefault()
      pick(hits[0].symbol)
    } else if (event.key === 'Escape') {
      event.stopPropagation()
      close(true)
    }
  }

  const rootStyle: CSSProperties | undefined = anchor
    ? ({ '--h': anchor.hue === '' ? undefined : anchor.hue } as CSSProperties)
    : undefined

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label="Trading pair"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? close(false) : openList())}
        className="flex w-full min-w-[130px] cursor-pointer items-center gap-2.5 border border-paper-35 bg-transparent px-2.5 py-[5px] text-paper hover:border-accent-line"
      >
        <span className="font-sans text-[13px] font-bold tracking-[0.12em]">{value ?? '—'}</span>
        <span className="min-w-0 flex-1 truncate text-left font-mono text-[10px] tracking-[0.1em] text-paper-40 uppercase">
          {current ? `${current.venue} · ${current.group}` : ''}
        </span>
        {badge !== null && current !== undefined && (
          <span data-pair-state={badge.word} title={badge.reason ?? undefined} className="shrink-0 font-mono text-[9px] tracking-[0.12em] text-paper-50 uppercase">
            {badge.word}
          </span>
        )}
        <span aria-hidden="true" className="font-mono text-[9px] text-paper-50">
          ▼
        </span>
      </button>
      {anchor !== null &&
        createPortal(
          <div style={rootStyle} data-quant-pair-popover>
            <div className="fixed inset-0 z-[60]" onClick={() => close(false)} aria-hidden="true" />
            <div
              style={{ left: anchor.left, top: anchor.top }}
              className="fixed z-[61] flex max-h-[420px] w-[330px] flex-col bg-paper text-ink shadow-[0_14px_40px_rgba(0,0,0,.45)]"
            >
              <div className="shrink-0 border-b border-ink-20 p-2.5">
                <input
                  ref={inputRef}
                  type="text"
                  aria-label="Search symbol"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Search symbol…"
                  className="w-full border border-ink-25 bg-transparent px-[9px] py-[7px] font-mono text-[12.5px] text-ink uppercase outline-none focus:border-accent-fill"
                />
              </div>
              <div id={listId} role="listbox" aria-label="Pairs" className="min-h-0 flex-1 overflow-y-auto pb-1.5">
                {groups.map((g) => (
                  <div key={g.group} role="group" aria-label={g.group}>
                    <div className="px-3 pt-2.5 pb-1 font-mono text-[9.5px] tracking-[0.2em] text-ink-50 uppercase">{g.group}</div>
                    {g.items.map((p) => {
                      const selected = p.symbol === value
                      return (
                        <button
                          key={p.symbol}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => pick(p.symbol)}
                          title={badge?.reason ?? undefined}
                          data-muted={badge?.muted ? 'true' : undefined}
                          className={cn(
                            'flex h-[34px] w-full cursor-pointer items-center gap-2.5 border-0 px-3 text-ink hover:bg-accent-wash',
                            badge?.muted && 'opacity-50',
                            selected ? 'bg-accent-wash shadow-[inset_3px_0_0_var(--accent-fill)]' : 'bg-transparent',
                          )}
                        >
                          <span className="min-w-[78px] text-left font-sans text-[12px] font-bold tracking-[0.1em]">{p.symbol}</span>
                          <span className="min-w-0 flex-1 truncate text-left font-mono text-[11px] text-ink-50" title={p.description}>
                            {p.description}
                          </span>
                          {badge !== null && (
                            <span data-pair-state={badge.word} className="shrink-0 font-mono text-[9.5px] tracking-[0.12em] text-ink-50 uppercase">
                              {badge.word}
                            </span>
                          )}
                          <span className="shrink-0 font-mono text-[9.5px] tracking-[0.12em] text-ink-50 uppercase">{p.venue}</span>
                        </button>
                      )
                    })}
                  </div>
                ))}
                {hits.length === 0 && (
                  <div className="px-3 py-4 font-mono text-[12px] text-ink-50">
                    {symbols.length === 0 && emptyText !== undefined ? emptyText : `No symbol matches "${query}".`}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
